import React, { useEffect, useState, useCallback } from "react";
import api, { apiError, inr } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, ReferenceLine,
} from "recharts";
import { toast } from "sonner";
import { ProfitShare, PaymentsReceived } from "@/components/finance/ProfitShare";
import {
  TrendingUp, TrendingDown, Wallet, Receipt, Percent, Plus, Trash2, IndianRupee,
} from "lucide-react";

const PERIODS = [
  { id: "day", label: "Today" },
  { id: "month", label: "This Month" },
  { id: "year", label: "This Year" },
  { id: "all", label: "All Time" },
];

export default function PnL() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", amount: "", category: "Other", contact_id: "none" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [pnl, exp] = await Promise.all([
      api.get(`/pnl?period=${period}`),
      api.get(`/expenses?period=${period}`),
    ]);
    setData(pnl.data);
    setExpenses(exp.data);
  }, [period]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/contacts").then((r) => setContacts(r.data));
    api.get("/expense-categories").then((r) => setCats(r.data));
  }, []);

  const addExpense = async () => {
    setSaving(true);
    try {
      await api.post("/expenses", {
        workspace_id: localStorage.getItem("workspace_id"),
        label: form.label,
        amount: parseFloat(form.amount),
        category: form.category,
        contact_id: form.contact_id === "none" ? null : form.contact_id,
      });
      toast.success("Expense recorded");
      setOpen(false);
      setForm({ label: "", amount: "", category: "Other", contact_id: "none" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeExpense = async (id) => {
    await api.delete(`/expenses/${id}`);
    load();
  };

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const profitPositive = data.net_profit >= 0;

  return (
    <div>
      <PageHeader eyebrow="Finance" title="Profit & Loss"
        subtitle="Per-hajji & per-associate-partner profit, expenses and net margin — daily, monthly, yearly"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-expense-btn" className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2">
                <Plus className="w-4 h-4" /> Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#05261D] border-gold/25" data-testid="expense-modal">
              <DialogHeader>
                <DialogTitle className="font-serif text-gold flex items-center gap-2"><Receipt className="w-5 h-5" /> Record Spend / Expense</DialogTitle>
                <DialogDescription>Log a cost. Link it to a hajji for per-customer profit, or leave blank for general overhead.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs text-muted-foreground">Label</Label>
                  <Input data-testid="expense-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. Hotel booking Makkah" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-muted-foreground">Amount ₹</Label>
                    <Input data-testid="expense-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 bg-card/60" /></div>
                  <div><Label className="text-xs text-muted-foreground">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-1 bg-card/60" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div><Label className="text-xs text-muted-foreground">Link to hajji (optional)</Label>
                  <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
                    <SelectTrigger className="mt-1 bg-card/60" data-testid="expense-contact-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">General / Overhead</SelectItem>
                      {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <DialogFooter>
                <Button data-testid="save-expense-btn" onClick={addExpense} disabled={saving || !form.label || !form.amount} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Saving…" : "Record Expense"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-4 sm:p-8 space-y-5">
        {/* Period toggle */}
        <div className="flex gap-2 flex-wrap">
          {PERIODS.map((p) => (
            <button key={p.id} data-testid={`period-${p.id}`} onClick={() => setPeriod(p.id)}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${period === p.id ? "bg-gold text-[#0a2e22] font-semibold gold-glow" : "bg-card/50 text-muted-foreground hover:text-white border border-border/60"}`}>
              {p.label}
            </button>
          ))}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Wallet} label="Money Received" value={inr(data.total_revenue)} color="text-white" sub={`Customers ${inr(data.customer_revenue)} · Members ${inr(data.member_revenue)}`} />
          <Kpi icon={Receipt} label="Expenses" value={inr(data.total_expense)} color="text-red-300" sub={`incl. ${inr(data.general_expense)} overhead`} />
          <Kpi icon={Percent} label="Associate Partner Commission" value={inr(data.total_commission)} color="text-amber-300" />
          <Card className={`p-4 bg-card/40 ${profitPositive ? "border-emerald-400/40" : "border-red-400/50"} gold-glow`}>
            <div className={`flex items-center gap-2 mb-2 ${profitPositive ? "text-emerald-300" : "text-red-300"}`}>
              {profitPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              <span className="eyebrow text-muted-foreground">Net {profitPositive ? "Profit" : "Loss"}</span>
            </div>
            <p data-testid="net-profit-value" className={`text-3xl font-serif font-semibold ${profitPositive ? "text-emerald-300" : "text-red-300"}`}>{inr(data.net_profit)}</p>
          </Card>
        </div>

        {/* Trend */}
        <div className="grid lg:grid-cols-2 gap-5">
          <ProfitShare data={data} />
          <PaymentsReceived data={data} onChanged={load} />
        </div>

        <Card className="p-5 bg-card/40 border-border/60">
          <p className="eyebrow text-gold mb-4 flex items-center gap-2"><IndianRupee className="w-4 h-4" /> Profit Trend ({period === "year" ? "monthly" : "daily"})</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.series}>
              <XAxis dataKey="label" stroke="#7a8f86" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#7a8f86" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#05261D", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, color: "#fff" }} cursor={{ fill: "rgba(212,175,55,0.06)" }} formatter={(v) => inr(v)} />
              <ReferenceLine y={0} stroke="#4b5563" />
              <Bar dataKey="profit" radius={[4, 4, 0, 0]}>
                {data.series.map((s, i) => <Cell key={i} fill={s.profit >= 0 ? "#34D399" : "#F87171"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <div className="grid lg:grid-cols-2 gap-5">
          {/* Per hajji */}
          <Card className="p-5 bg-card/40 border-border/60">
            <p className="eyebrow text-gold mb-3">Profit / Loss by Hajji</p>
            <div className="space-y-2 max-h-96 overflow-auto">
              {data.hajjis.length === 0 && <p className="text-xs text-muted-foreground py-6 text-center">No revenue or expenses in this period.</p>}
              {data.hajjis.map((h) => (
                <div key={h.contact_id} data-testid={`pnl-hajji-${h.contact_id}`} className="p-3 rounded-lg bg-card/50 border border-border/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{h.name}</p>
                      <p className="text-[10px] text-muted-foreground">Partner: {h.agent_name}</p>
                    </div>
                    <span className={`font-serif text-lg ${h.profit >= 0 ? "text-emerald-300" : "text-red-300"}`}>{inr(h.profit)}</span>
                  </div>
                  <div className="flex gap-4 mt-1.5 text-[11px] text-muted-foreground font-mono">
                    <span>Rev {inr(h.revenue)}</span>
                    <span className="text-red-300/80">Exp {inr(h.expense)}</span>
                    <span className="text-amber-300/80">Comm {inr(h.commission)}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Per agent */}
          <Card className="p-5 bg-card/40 border-border/60">
            <p className="eyebrow text-gold mb-3">Profit / Loss by Associate Partner</p>
            <div className="space-y-2 max-h-96 overflow-auto">
              {data.agents.length === 0 && <p className="text-xs text-muted-foreground py-6 text-center">No associate partners with activity.</p>}
              {data.agents.map((a) => (
                <div key={a.agent_id} data-testid={`pnl-agent-${a.agent_id}`} className="p-3 rounded-lg bg-card/50 border border-border/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{a.agent_name}</p>
                      <p className="text-[10px] text-muted-foreground">{a.customers} hajji · commission earned {inr(a.commission_earned)}</p>
                    </div>
                    <span className={`font-serif text-lg ${a.company_profit >= 0 ? "text-emerald-300" : "text-red-300"}`}>{inr(a.company_profit)}</span>
                  </div>
                  <div className="flex gap-4 mt-1.5 text-[11px] text-muted-foreground font-mono">
                    <span>Rev {inr(a.revenue)}</span>
                    <span className="text-red-300/80">Exp {inr(a.expense)}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Expense ledger */}
        <Card className="p-5 bg-card/40 border-border/60">
          <p className="eyebrow text-gold mb-3">Recorded Expenses ({PERIODS.find((p) => p.id === period)?.label})</p>
          <div className="space-y-2">
            {expenses.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No expenses recorded. Use “Add Expense”.</p>}
            {expenses.map((e) => (
              <div key={e.id} data-testid={`expense-row-${e.id}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-card/50 border border-border/50">
                <Badge variant="outline" className="border-gold/40 text-gold text-[10px]">{e.category}</Badge>
                <span className="text-sm flex-1 truncate">{e.label}</span>
                <span className="text-xs text-muted-foreground">{new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                <span className="font-mono text-red-300 text-sm w-24 text-right">{inr(e.amount)}</span>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" data-testid={`del-expense-${e.id}`} onClick={() => removeExpense(e.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color, sub }) {
  return (
    <Card className="p-4 bg-card/40 border-border/60 card-hover">
      <div className="flex items-center gap-2 text-gold mb-2"><Icon className="w-4 h-4" /><span className="eyebrow text-muted-foreground">{label}</span></div>
      <p className={`text-2xl font-serif font-semibold ${color}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </Card>
  );
}
